import { Box, Button, Card, CardContent, IconButton, Menu, MenuItem } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDownOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CSSProperties, useMemo, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TagChip } from "../components/TagChip";
import { RecurringPaymentEditor } from "../features/recurring/RecurringPaymentEditor";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { financeApi } from "../lib/api";
import { appToday, useDevDateOverride } from "../lib/devDate";
import { CategoryIcon, currencySymbol, dateInputValue, money } from "../lib/format";
import { fallbackCurrencies, recurrenceFrequencyUnits } from "../lib/labels";
import { queryKeys } from "../lib/queryKeys";
import type { Category, Group, RecurringPayment, Tag } from "../lib/types";

const WEEKS_PER_MONTH = 52 / 12;
const UPCOMING_DUE_CHIP_DAYS = 7;

function monthlyEquivalent(payment: RecurringPayment) {
  const amount = Number(payment.amount);
  if (!Number.isFinite(amount) || payment.interval <= 0) return 0;
  if (payment.frequency === "weekly") return (amount * WEEKS_PER_MONTH) / payment.interval;
  if (payment.frequency === "monthly") return amount / payment.interval;
  return amount / (12 * payment.interval);
}

function frequencyLabel(payment: RecurringPayment) {
  const unit = recurrenceFrequencyUnits[payment.frequency];
  return payment.interval === 1 ? `Every ${unit}` : `Every ${payment.interval} ${unit}s`;
}

function startOfToday() {
  const today = appToday();
  today.setHours(0, 0, 0, 0);
  return today;
}

function dueDayOffset(payment: RecurringPayment) {
  const due = new Date(`${payment.next_due_date}T00:00:00`);
  return Math.round((due.getTime() - startOfToday().getTime()) / 86_400_000);
}

function dueDateLabel(payment: RecurringPayment) {
  const due = new Date(`${payment.next_due_date}T00:00:00`);
  const withYear = due.getFullYear() !== appToday().getFullYear();
  return due.toLocaleDateString(undefined, { month: "short", day: "numeric", year: withYear ? "numeric" : undefined });
}

function todayDateLabel() {
  return appToday().toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function recurringAmountText(payment: RecurringPayment) {
  return `${payment.type === "income" ? "+" : "−"}${currencySymbol(payment.currency)}${money(payment.amount)}`;
}

function DueChip({ payment }: { payment: RecurringPayment }) {
  if (payment.is_paused) return <span className="recurring-due-chip paused">Paused</span>;
  const offset = dueDayOffset(payment);
  if (offset < 0) {
    const days = Math.abs(offset);
    return <span className="recurring-due-chip overdue">Overdue by {days} {days === 1 ? "day" : "days"} · {dueDateLabel(payment)}</span>;
  }
  if (offset === 0) return <span className="recurring-due-chip today">Today</span>;
  if (offset === 1) return <span className="recurring-due-chip">Tomorrow</span>;
  if (offset <= UPCOMING_DUE_CHIP_DAYS) {
    return <span className="recurring-due-chip upcoming">In {offset} days · {dueDateLabel(payment)}</span>;
  }
  return <span className="recurring-due-chip">{dueDateLabel(payment)}</span>;
}

function ForecastSummary({ payments }: { payments: RecurringPayment[] }) {
  const totals = new Map<string, { income: number; expense: number }>();
  for (const payment of payments) {
    const entry = totals.get(payment.currency) || { income: 0, expense: 0 };
    entry[payment.type === "income" ? "income" : "expense"] += monthlyEquivalent(payment);
    totals.set(payment.currency, entry);
  }
  const rows = [...totals.entries()];
  if (!rows.length) return null;
  const line = (value: (entry: { income: number; expense: number }) => number, sign: string) =>
    rows
      .filter(([, entry]) => value(entry) !== 0)
      .map(([currency, entry]) => `${sign}${currencySymbol(currency)}${money(Math.abs(value(entry)))}`)
      .join(" · ") || "—";
  return (
    <Box className="recurring-summary">
      <Box className="recurring-summary-card">
        <span className="recurring-summary-label">Income / month</span>
        <span className="recurring-summary-value income">{line((entry) => entry.income, "+")}</span>
      </Box>
      <Box className="recurring-summary-card">
        <span className="recurring-summary-label">Expenses / month</span>
        <span className="recurring-summary-value expense">{line((entry) => entry.expense, "−")}</span>
      </Box>
      <Box className="recurring-summary-card">
        <span className="recurring-summary-label">Net / month</span>
        <span className="recurring-summary-value">
          {line((entry) => entry.income - entry.expense, "")}
        </span>
      </Box>
    </Box>
  );
}

function PaymentTypeIcon({ payment, category }: { payment: RecurringPayment; category?: Category }) {
  if (category) return <CategoryIcon icon={category.icon} color={category.color} />;
  return (
    <span className="category-icon">
      {payment.type === "income" ? <TrendingUpIcon aria-hidden="true" /> : <TrendingDownIcon aria-hidden="true" />}
    </span>
  );
}

function isTag(value: Tag | undefined): value is Tag {
  return Boolean(value);
}

export function RecurringPaymentsPage({ group, onBlockedAccess }: { group: Group; onBlockedAccess?: () => void }) {
  const queryClient = useQueryClient();
  const asOfDate = useDevDateOverride();
  const [status, setStatus] = useState("");
  const [editorState, setEditorState] = useState<{ open: boolean; payment: RecurringPayment | null }>({ open: false, payment: null });
  const [confirmPayment, setConfirmPayment] = useState<RecurringPayment | null>(null);
  const [confirmDeletePayment, setConfirmDeletePayment] = useState<RecurringPayment | null>(null);
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; payment: RecurringPayment } | null>(null);

  const paymentsQuery = useQuery({ queryKey: [...queryKeys.recurringPayments(group.id), asOfDate], queryFn: () => financeApi.recurringPayments(group.id) });
  const accountsQuery = useQuery({ queryKey: queryKeys.accounts(group.id), queryFn: () => financeApi.accounts(group.id) });
  const categoriesQuery = useQuery({ queryKey: queryKeys.categories(group.id), queryFn: () => financeApi.categories(group.id) });
  const tagsQuery = useQuery({ queryKey: queryKeys.tags(group.id), queryFn: () => financeApi.tags(group.id) });
  const currenciesQuery = useQuery({ queryKey: queryKeys.currencies(group.id), queryFn: () => financeApi.currencies(group.id) });
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id) });

  useBlockedAccessGuard([paymentsQuery.error, accountsQuery.error, tagsQuery.error], onBlockedAccess);

  const payments = paymentsQuery.data || [];
  const accounts = accountsQuery.data || [];
  const categories = categoriesQuery.data || [];
  const tags = tagsQuery.data || [];
  const currencies = currenciesQuery.data?.length ? currenciesQuery.data : fallbackCurrencies;
  const members = membersQuery.data || [];

  const activePayments = payments.filter((payment) => !payment.is_archived && !payment.is_paused);
  const pausedPayments = payments.filter((payment) => !payment.is_archived && payment.is_paused);
  const deletedPayments = payments.filter((payment) => payment.is_archived);
  const recurringAmountCh = useMemo(
    () => Math.max(8, ...payments.map((payment) => recurringAmountText(payment).length)),
    [payments],
  );

  const invalidate = async (withRecords = false) => {
    const invalidations = [queryClient.invalidateQueries({ queryKey: queryKeys.recurringPayments(group.id) })];
    if (withRecords) {
      invalidations.push(
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      );
    }
    await Promise.all(invalidations);
  };

  const runMutation = { onError: (error: unknown) => setStatus(error instanceof Error ? error.message : "Request failed") };
  const confirmMutation = useMutation({
    mutationFn: (id: string) => financeApi.confirmRecurringPayment(group.id, id, { occurred_on: dateInputValue(appToday()) }),
    onSuccess: async () => {
      setConfirmPayment(null);
      setStatus("");
      await invalidate(true);
    },
    ...runMutation,
  });
  const skipMutation = useMutation({
    mutationFn: (id: string) => financeApi.skipRecurringPayment(group.id, id),
    onSuccess: async () => {
      setStatus("");
      await invalidate();
    },
    ...runMutation,
  });
  const pauseMutation = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      paused ? financeApi.resumeRecurringPayment(group.id, id) : financeApi.pauseRecurringPayment(group.id, id),
    onSuccess: async () => {
      setStatus("");
      await invalidate();
    },
    ...runMutation,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => financeApi.deleteRecurringPayment(group.id, id),
    onSuccess: async () => {
      setConfirmDeletePayment(null);
      setStatus("");
      await invalidate();
    },
    ...runMutation,
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => financeApi.restoreRecurringPayment(group.id, id),
    onSuccess: async () => {
      setStatus("");
      await invalidate();
    },
    ...runMutation,
  });

  const closeActionMenu = () => setActionMenu(null);

  const renderRow = (payment: RecurringPayment) => {
    const category = categories.find((item) => item.id === payment.category_id);
    const account = accounts.find((item) => item.id === payment.account_id);
    const paymentTags = (payment.tag_ids || []).map((tagId) => tags.find((item) => item.id === tagId)).filter(isTag);
    const meta = [frequencyLabel(payment), category?.name, account?.name].filter(Boolean).join(" · ");
    const signClass = payment.type === "income" ? "income" : "expense";
    return (
      <div
        key={payment.id}
        className={`recurring-row${payment.is_paused ? " paused" : ""}${payment.is_archived ? " archived" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => !payment.is_archived && setEditorState({ open: true, payment })}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !payment.is_archived) {
            event.preventDefault();
            setEditorState({ open: true, payment });
          }
        }}
      >
        <PaymentTypeIcon payment={payment} category={category} />
        <div className="recurring-row-main">
          <span className="recurring-row-name">{payment.name}</span>
          <span className="recurring-row-meta">{meta}</span>
          {paymentTags.length ? (
            <span className="recurring-row-tags">
              {paymentTags.map((tag) => (
                <TagChip key={tag.id} tag={tag} />
              ))}
            </span>
          ) : null}
        </div>
        <DueChip payment={payment} />
        <span className={`recurring-amount ${signClass}`}>
          {payment.type === "income" ? "+" : "−"}
          {currencySymbol(payment.currency)}
          {money(payment.amount)}
        </span>
        <span className="recurring-row-actions" onClick={(event) => event.stopPropagation()}>
          {payment.is_archived ? (
            <Button className="recurring-inline-action" variant="text" type="button" disabled={restoreMutation.isPending} onClick={() => restoreMutation.mutate(payment.id)}>
              Restore
            </Button>
          ) : (
            <>
              {!payment.is_paused ? (
                <Button className="recurring-inline-action primary" variant="outlined" type="button" onClick={() => setConfirmPayment(payment)}>
                  Add record
                </Button>
              ) : (
                <Button
                  className="recurring-inline-action"
                  variant="text"
                  type="button"
                  disabled={pauseMutation.isPending}
                  onClick={() => pauseMutation.mutate({ id: payment.id, paused: payment.is_paused })}
                >
                  Resume
                </Button>
              )}
            </>
          )}
          <IconButton
            className="records-icon-button recurring-more-button"
            type="button"
            aria-label={`More actions for ${payment.name}`}
            title="More actions"
            onClick={(event) => setActionMenu({ anchor: event.currentTarget, payment })}
          >
            <MoreHorizOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </div>
    );
  };

  const menuPayment = actionMenu?.payment || null;

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>Recurring payments</h1>
          <p>Forecast regular income and expenses. Confirm a payment to add it to Records.</p>
        </Box>
      </header>

      <section className="content">
        <ForecastSummary payments={[...activePayments, ...pausedPayments]} />
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            <Box className="records-toolbar">
              <Box className="records-primary-actions">
                <Button variant="contained" type="button" startIcon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setEditorState({ open: true, payment: null })}>
                  Add payment
                </Button>
              </Box>
            </Box>
            {status ? <p className="status error">{status}</p> : null}
            {paymentsQuery.isLoading ? <p className="status">Loading recurring payments...</p> : null}
            {!paymentsQuery.isLoading && !payments.length ? (
              <p className="users-group-empty">No recurring payments yet. Add a subscription or a salary to see the monthly forecast.</p>
            ) : null}
            <div className="recurring-list" style={{ "--recurring-amount-ch": `${recurringAmountCh}ch` } as CSSProperties}>
              {activePayments.map(renderRow)}
              {pausedPayments.length ? <p className="recurring-section-label">Paused</p> : null}
              {pausedPayments.map(renderRow)}
              {deletedPayments.length ? <p className="recurring-section-label">Deleted</p> : null}
              {deletedPayments.map(renderRow)}
            </div>
          </CardContent>
        </Card>
      </section>

      <Menu
        anchorEl={actionMenu?.anchor || null}
        open={Boolean(actionMenu)}
        className="recurring-actions-menu"
        onClose={closeActionMenu}
      >
        {menuPayment && !menuPayment.is_archived && !menuPayment.is_paused ? (
          <MenuItem
            disabled={skipMutation.isPending}
            onClick={() => {
              skipMutation.mutate(menuPayment.id);
              closeActionMenu();
            }}
          >
            Skip this due date
          </MenuItem>
        ) : null}
        {menuPayment && !menuPayment.is_archived && !menuPayment.is_paused ? (
          <MenuItem
            disabled={pauseMutation.isPending}
            onClick={() => {
              pauseMutation.mutate({ id: menuPayment.id, paused: menuPayment.is_paused });
              closeActionMenu();
            }}
          >
            Pause
          </MenuItem>
        ) : null}
        {menuPayment && !menuPayment.is_archived ? (
          <MenuItem
            disabled={deleteMutation.isPending}
            onClick={() => {
              deleteMutation.mutate(menuPayment.id);
              closeActionMenu();
            }}
          >
            Delete
          </MenuItem>
        ) : null}
        {menuPayment?.is_archived ? (
          <MenuItem
            disabled={deleteMutation.isPending}
            onClick={() => {
              setConfirmDeletePayment(menuPayment);
              closeActionMenu();
            }}
          >
            Delete forever
          </MenuItem>
        ) : null}
      </Menu>

      {editorState.open ? (
        <RecurringPaymentEditor
          group={group}
          payment={editorState.payment}
          accounts={accounts}
          categories={categories}
          tags={tags}
          members={members}
          currencies={currencies}
          onClose={() => setEditorState({ open: false, payment: null })}
        />
      ) : null}
      {confirmPayment ? (
        <ConfirmDialog
          titleId="confirmRecurringPaymentTitle"
          title="Confirm payment?"
          description={`A ${confirmPayment.type} record for ${currencySymbol(confirmPayment.currency)}${money(confirmPayment.amount)} will be added for today (${todayDateLabel()}) and the next due date will move forward.`}
          confirmLabel="Add record"
          closeAriaLabel="Close confirm payment dialog"
          pending={confirmMutation.isPending}
          onConfirm={() => confirmMutation.mutate(confirmPayment.id)}
          onClose={() => setConfirmPayment(null)}
        />
      ) : null}
      {confirmDeletePayment ? (
        <ConfirmDialog
          titleId="deleteRecurringPaymentForeverTitle"
          title="Delete payment forever?"
          description={`"${confirmDeletePayment.name}" will be removed permanently. Records created from it stay in place.`}
          confirmLabel="Delete forever"
          closeAriaLabel="Close delete payment dialog"
          pending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(confirmDeletePayment.id)}
          onClose={() => setConfirmDeletePayment(null)}
        />
      ) : null}
    </>
  );
}
